import {
  ACTIVE_AUDIENCE,
  AUDIENCE_COMPLIANCE_STATUS,
  AUDIENCE_EXPORT_LAL_MODAL_TYPE,
  AUDIENCE_EXPORT_TYPES,
  ENUM_SYSTEM_USED,
  EXPORT_MODELED_AUDIENCES,
  EXPORT_TYPES_FIELDS_REQUIRED,
  LoadingStates,
  META_AUDIENCE_SIZE_EXPORT_THRESHOLD,
  PLATFORMS_KEYS,
  REQUIRED_FIELDS_ARE_MISSING,
  TIKTOK_AUDIENCE_SIZE_EXPORT_THRESHOLD,
  ValidationErrorMessages,
  isKdsNonProductionBuild,
} from '../../../constants';
import { Button } from '@attain-sre/attain-design-system';
import {
  ToggleExportDialogAction,
  addNotification,
  exportAudience,
  fetchAudience,
  toggleAudienceExporting,
  toggleExportDialog,
} from '../slice';

import { fetchCustomAudience } from '../../customAudiences/slice';

import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useUser } from 'reactfire';
// Material UI Components
import AudienceExport from '../../../components/AudienceExport/InhouseAudienceExport';
import SeedAudienceExport from '../../../components/AudienceExport/InHouseSeedAudienceExport';

import ComplianceWarningModal from '../../../components/ComplianceWarningModal';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import InlineExportHistory from './InlineExportHistory';
import Typography from '@material-ui/core/Typography';
import _ from 'lodash';
import axios from 'axios';
import {
  ACTIVE_AUDIENCES,
  AUDIENCE_EXPORT_FAILED_MESSAGE,
  AUDIENCE_EXPORT_SUCCESS_MESSAGE,
  COMPLIANCE_WARNING_BLOCKED_NO_BAN_ROWS,
  COMPLIANCE_WARNING_POLICY_FULL_BAN,
  COMPLIANCE_WARNING_POLICY_PARTIAL_BAN,
  NEXXEN_AVAILABILITY_ALL,
  NEXXEN_AVAILABILITY_SINGLE,
  NEXXEN_NONPROD_DEFAULT_ADVERTISER_ID,
  NEXXEN_NONPROD_DEFAULT_MARKET_ID,
  NEXXEN_RATE_TYPE_CPM,
  NEXXEN_RATE_TYPE_CPMAP,
  NEXXEN_RATE_TYPE_FF,
  NEXXEN_RATE_TYPE_PM,
} from '../../../content';
import { API_BASE_ADDRESS, AUDIENCE_STUDIO_API_ENDPOINT } from '../variables';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  areDentsuOtsRequiredFieldsMissing,
  areWppOtsRequiredFieldsMissing,
  isAdvertiserDirectValid,
  isCPMValid,
  isCurrencyRateValid,
  isDescriptionValid,
  isFloat,
  isNexxenDatesValid,
  isPercentageOfMediaValid,
  isSegmentNameValid,
  isTTDIdValid,
  isTvCpmValid,
  isViantIdValid,
  validateNexxenAdvertiserDestinationId,
  validateNexxenMarketDestinationId,
} from '../../../helper/helper';
import { getAudienceExportFields } from './audienceExportSegmentConfig';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(3),
    },
    email: {
      marginTop: '24px',
      marginBottom: theme.spacing(3),
      marginLeft: '6px',
    },
    title: {
      fontFamily: 'Calibre-Semibold,sans-serif',
      fontWeight: 600,
      fontSize: 22,
      lineHeight: '32px',
      minWidth: 1000,
    },
    exportTitle: {
      fontFamily: 'Calibre-Semibold,sans-serif',
      fontWeight: 600,
      fontSize: 22,
      lineHeight: '32px',
      minWidth: 1000,
      margin: '1rem 0 0 1.5rem',
    },
    selectExport: {
      width: '100%',
    },
    controlLabel: {
      fontFamily: 'Calibre-Semibold,sans-serif',
      fontSize: 16,
      fontWeight: 500,
      paddingBottom: 4,
    },
    emailLabel: {
      fontFamily: 'Calibre-Semibold,sans-serif',
      fontSize: 16,
      fontWeight: 500,
      paddingBottom: 10,
      paddingTop: 40,
    },
    exportSeedAudienceContainer: {
      paddingTop: 20,
      backgroundColor: 'rgb(240, 239, 241)',
      padding: 15,
      marginTop: 36,
      borderRadius: 8,
      width: '100%',
    },
    divider: {
      border: '1px solid #e4e4e4',
      marginTop: 20,
      width: '100%',
    },
    exportExportButton: {
      textAlign: 'right',
      marginTop: 15,
    },
  })
);

interface Props {
  lalModelType: string;
  lalModelPrecision?: any;
  audienceName: string;
  audienceCountTotals: any;
  audienceType: string;
  isSelectAudienceVisible: boolean;
  modelScoringType: string;
}

/**
 * Maps Nexxen rate type to the appropriate payload fields
 * @param rateType - The rate type (CPM, PM, CPMAP, FF)
 * @param currencyRate - The currency rate value
 * @returns Object with cpm, percentageOfMedia, and costPerAction fields
 */
const mapNexxenRateTypeToPayload = (rateType: string, currencyRate: string) => {
  const rate = parseFloat(currencyRate || '0');
  const formattedRate = rate > 0 ? parseFloat(rate.toFixed(2)) : 0;

  const rateFields = {
    cpm: null as number | null,
    percentageOfMedia: null as number | null,
    costPerAction: null as number | null,
  };

  switch (rateType) {
    case NEXXEN_RATE_TYPE_CPM:
      rateFields.cpm = formattedRate;
      break;
    case NEXXEN_RATE_TYPE_PM:
      rateFields.percentageOfMedia = formattedRate;
      break;
    case NEXXEN_RATE_TYPE_CPMAP:
      rateFields.costPerAction = formattedRate;
      break;
    case NEXXEN_RATE_TYPE_FF:
      rateFields.cpm = null;
      rateFields.percentageOfMedia = null;
      rateFields.costPerAction = null;
      break;
  }

  return rateFields;
};

const resolveAttainLalModelId = (
  audience:
    | {
        lalModelId?: string | null;
        lal_model_id?: string | null;
      }
    | null
    | undefined
): string | undefined => {
  const camel =
    typeof audience?.lalModelId === 'string' ? audience.lalModelId.trim() : '';
  if (camel) {
    return camel;
  }
  const snake =
    typeof audience?.lal_model_id === 'string'
      ? audience.lal_model_id.trim()
      : '';
  return snake || undefined;
};

/**
 * Prepares Nexxen export payload
 * @param params - Object containing all necessary parameters for Nexxen export
 * @returns Nexxen payload object
 */
const prepareNexxenPayload = ({
  audience,
  currentUser,
  segmentName,
  lalModelType,
  exportType,
  lalModelPrecisionUsersCount,
  lalModelPrecision,
  audienceType,
  currentFormData,
  seedAudienceCount,
  modelScoringType,
  lalModelId,
}: {
  audience: any;
  currentUser: any;
  segmentName: string;
  lalModelType: string;
  exportType: string;
  lalModelPrecisionUsersCount: any;
  lalModelPrecision: any;
  audienceType: string;
  currentFormData: any;
  seedAudienceCount: number | null;
  modelScoringType: string;
  lalModelId?: string;
}) => {
  const nexxenPayload: any = {
    id: audience.id,
    emails: currentUser.email,
    segmentName: segmentName?.trim(),
    lalModelType,
    exportType,
    platformIntegrated: PLATFORMS_KEYS.NEXXEN_KEY,
    lalModelPrecisionUsersCount: lalModelPrecisionUsersCount,
    lalModelPrecision: lalModelPrecision,
    startDate: currentFormData?.startDate,
    endDate: currentFormData?.endDate || null,
    audienceType: audienceType,
    rateType: currentFormData?.rateType || '',
    seedAudienceCount,
    modelScoringType,
    ...(lalModelId ? { lalModelId } : {}),
  };

  // Add availability for Custom segment exports (for backend validation)
  if (exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT) {
    nexxenPayload.availability = currentFormData?.availability || '';

    // Add advertiser and platform destination only when availability is SINGLE
    if (currentFormData?.availability === NEXXEN_AVAILABILITY_SINGLE) {
      nexxenPayload.advertiserDestination =
        currentFormData?.advertiserDestination?.trim() || '';
      nexxenPayload.platformDestination =
        currentFormData?.platformDestination?.trim() || '';
    }
  }

  // Map currency rate to appropriate field based on rate type
  const rateFields = mapNexxenRateTypeToPayload(
    currentFormData?.rateType,
    currentFormData?.currencyRate
  );
  Object.assign(nexxenPayload, rateFields);

  return nexxenPayload;
};

const InlineAudienceExport = ({
  lalModelType = 'transunion',
  lalModelPrecision,
  audienceName,
  audienceCountTotals,
  audienceType,
  isSelectAudienceVisible,
  modelScoringType,
}: Props) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  // Retrieve the current audience details
  const audience = useAppSelector((state) => {
    if (audienceType === ENUM_SYSTEM_USED.STANDARD_AUDIENCE) {
      if (state.audienceReducer && state.audienceReducer.currentAudience) {
        return state.audienceReducer.currentAudience;
      } else {
        return null;
      }
    } else {
      if (
        state.customAudienceReducer &&
        state.customAudienceReducer.currentAudience
      ) {
        return state.customAudienceReducer.currentAudience;
      } else {
        return null;
      }
    }
  });

  const lalModelPrecisionUsersCount = audienceCountTotals
    ? audienceCountTotals[lalModelPrecision]
    : null;

  const { data: user } = useUser();
  const currentUser = user || { email: '' };
  const [emails, setEmails] = useState(currentUser.email);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedExportTypes, setSelectedExportTypes] = useState([]);
  const [selectedSeedAudienceExportTypes, setSelectedSeedAudienceExportTypes] =
    useState([]);
  const [exportDefaultFields, setExportDefaultFields] = useState({
    segmentName: audienceName,
    description: '',
    cpm: '',
    percentageOfMedia: '',
  });
  const [exportDynamicFields, setExportDynamicFields] = useState([]);
  const [exportDynamicValues, setExportDynamicValues] = useState([]);
  const [complianceWarningOpen, setComplianceWarningOpen] = useState(false);
  const [complianceWarningTableData, setComplianceWarningTableData] = useState<
    Array<{
      category: string;
      policy_label: string;
      matched_keywords: string[];
      matched_brands: string[];
    }>
  >([]);

  const { segmentName, description, cpm, percentageOfMedia } =
    exportDefaultFields;

  useEffect(() => {
    // Prepare UI fields based on the selected export types
    const allExports = [
      ...selectedExportTypes,
      ...selectedSeedAudienceExportTypes,
    ];
    const preparedUIFields = getAudienceExportFields(allExports);
    setExportDynamicFields(preparedUIFields);

    // Prepare form data for each UI field
    const formDataFields = initializeFormDataFields(preparedUIFields);
    setExportDynamicValues(formDataFields);
  }, [selectedExportTypes, selectedSeedAudienceExportTypes]);

  /* Handler functions | Start */

  const dispatchClose = () => {
    const actionProps: ToggleExportDialogAction = {
      open: false,
    };
    dispatch(toggleExportDialog(actionProps));
  };

  const handleSelectedExportType = (exportTypes: Array<string>) => {
    setSelectedExportTypes(exportTypes);
  };

  const handleSelectedSeedAudienceExportType = (exportTypes: Array<string>) => {
    setSelectedSeedAudienceExportTypes(exportTypes);
  };

  const handleChangeField = (id: string, value: any, segmentId: string) => {
    if (segmentId) {
      // If segmentId exists, update the export dynamic fields
      const updatedForm = updateFormData(
        exportDynamicValues,
        id,
        value,
        segmentId
      );
      setExportDynamicValues(updatedForm);
    } else {
      setExportDefaultFields({ ...exportDefaultFields, [id]: value });
    }
  };

  const updateFormData = (
    formData: any[],
    id: string,
    value: any,
    segmentId: string
  ) => {
    // Find the existing form data by segmentId
    const existingFormData = formData.find((x) => x.id === segmentId);
    if (existingFormData) {
      existingFormData[id] = value; // Update the field value

      const isNexxenExport = [
        AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT,
        AUDIENCE_EXPORT_TYPES.NEXXEN_OTS_SEGMENT,
      ].includes(existingFormData.exportType);
      switch (id) {
        case 'platformDestination':
          if (!isNexxenExport) {
            existingFormData.advertiserDestination = '';
          }
          break;
        case 'advertiserDestination':
          if (!isNexxenExport) {
            if (!value) existingFormData.platformDestination = '';
          }
          break;
        case 'availability':
          if (value !== NEXXEN_AVAILABILITY_SINGLE) {
            existingFormData.advertiserDestination = '';
            existingFormData.platformDestination = '';
          }
          break;
        case 'rateType':
          if (value === NEXXEN_RATE_TYPE_FF) {
            existingFormData.currencyRate = '';
          }
          break;
        default:
          break;
      }

      return [...formData]; // Return a new array with the updated form data
    }

    return formData;
  };

  const startExport = () => {
    if (audience) {
      const allExports = [
        ...selectedExportTypes,
        ...selectedSeedAudienceExportTypes,
      ];

      // Check Meta Seed Export minimum user requirement
      // Meta requires "over 300" (i.e., > 300), so we reject if <= 300
      if (
        allExports.includes(
          AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT_SEED_AUDIENCE
        )
      ) {
        if (
          (audience?.totalSize || audience?.size || 0) <=
          META_AUDIENCE_SIZE_EXPORT_THRESHOLD
        ) {
          dispatch(
            addNotification({
              state: LoadingStates.ERROR,
              message:
                ValidationErrorMessages.META_SEED_EXPORT_MINIMUM_USERS_ERROR.replace(
                  '{{MIN_USER_COUNT}}',
                  META_AUDIENCE_SIZE_EXPORT_THRESHOLD.toString()
                ),
            })
          );
          return;
        }
      }

      // Check TikTok Seed Export minimum user requirement
      if (
        allExports.includes(
          AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT_SEED_AUDIENCE
        )
      ) {
        if (
          (audience?.totalSize || audience?.size || 0) <
          TIKTOK_AUDIENCE_SIZE_EXPORT_THRESHOLD
        ) {
          dispatch(
            addNotification({
              state: LoadingStates.ERROR,
              message:
                ValidationErrorMessages.TIKTOK_SEED_EXPORT_MINIMUM_USERS_ERROR.replace(
                  '{{MIN_USER_COUNT}}',
                  TIKTOK_AUDIENCE_SIZE_EXPORT_THRESHOLD.toString()
                ),
            })
          );
          return;
        }
      }

      const cpmValidFor = [
        ...EXPORT_TYPES_FIELDS_REQUIRED.cpm,
        ...EXPORT_TYPES_FIELDS_REQUIRED.default,
      ];
      const pomValidFor = [
        ...EXPORT_TYPES_FIELDS_REQUIRED.percentageOfMedia,
        ...EXPORT_TYPES_FIELDS_REQUIRED.default,
      ];

      const isCPM = cpmValidFor.some((x) => allExports.includes(x));
      const isPOM = pomValidFor.some((x) => allExports.includes(x));

      if (isCPM && cpm !== '' && !isFloat(cpm)) {
        dispatch(
          addNotification({
            state: LoadingStates.ERROR,
            message: 'Please enter valid CPM',
          })
        );
        return;
      }
      if (isPOM && percentageOfMedia !== '' && !isFloat(percentageOfMedia)) {
        dispatch(
          addNotification({
            state: LoadingStates.ERROR,
            message: 'Please enter valid % of media',
          })
        );
        return;
      }
      handleExportToTransunion(false);
    }
  };

  const handleComplianceWarningClose = () => {
    setComplianceWarningOpen(false);
    setComplianceWarningTableData([]);
  };

  const handleComplianceWarningCancel = () => {
    setComplianceWarningOpen(false);
    setComplianceWarningTableData([]);
  };

  const handleComplianceWarningConfirm = () => {
    setComplianceWarningOpen(false);
    setComplianceWarningTableData([]);
    handleExportToTransunion(true);
  };

  /* Handler functions | End */

  /* Util functions | Start */

  const initializeFormDataFields = (fields: any[]) => {
    return fields.map((field) => {
      // Get column names from the field form JSON
      const columnNames =
        field.formJSON
          ?.filter((column) => column.column)
          .map((column) => column.column) || [];

      // Initialize form data with the field id and export type
      const formData = { id: field.id, exportType: field.exportType };

      // Add each column as a field in the form data, using default value from config if available
      if (columnNames.length > 0) {
        columnNames.forEach((column) => {
          const fieldConfig = field.formJSON?.find(
            (f: any) => f.column === column
          );
          // Use default value from config if available, otherwise empty string
          formData[column] =
            fieldConfig?.value !== undefined ? fieldConfig.value : '';
        });
      }

      if (
        field.exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT &&
        isKdsNonProductionBuild()
      ) {
        formData.advertiserDestination = NEXXEN_NONPROD_DEFAULT_ADVERTISER_ID;
        formData.platformDestination = NEXXEN_NONPROD_DEFAULT_MARKET_ID;
      }

      return formData;
    });
  };

  const clearData = (isSuccess: boolean) => {
    setIsExporting(false);
    dispatchClose();
    if (isSuccess) {
      setExportDynamicFields([]);
      setSelectedExportTypes([]);
      setSelectedSeedAudienceExportTypes([]);
      setExportDynamicValues([]);
      setExportDefaultFields({
        segmentName: audienceName,
        description: '',
        cpm: '',
        percentageOfMedia: '',
      });
    }
  };

  const prepareExportPayload = () => {
    const resultPayload = [];
    const allExports = [
      ...selectedExportTypes,
      ...selectedSeedAudienceExportTypes,
    ];
    const seedAudienceCount = audience?.totalSize ?? audience?.size ?? null;

    for (const exportType of allExports) {
      const isSeedAudienceExport =
        selectedSeedAudienceExportTypes.includes(exportType);

      const payload: Record<string, unknown> = {
        id: audience.id,
        emails: currentUser.email,
        segmentName,
        description: description?.trim(),
        cpm: cpm !== '' ? parseFloat(cpm) : '',
        percentageOfMedia:
          percentageOfMedia !== '' ? parseFloat(percentageOfMedia) : '',
        lalModelType: lalModelType,
        lalModelPrecisionUsersCount: lalModelPrecisionUsersCount,
        lalModelPrecision:
          lalModelPrecision !== '' ? parseFloat(lalModelPrecision) : '',
        seedAudienceCount,
      };

      if (!isSeedAudienceExport) {
        payload.modelScoringType = modelScoringType;
      }
      if (
        !isSeedAudienceExport &&
        lalModelType === AUDIENCE_EXPORT_LAL_MODAL_TYPE.ATTAIN
      ) {
        const modelId = resolveAttainLalModelId(audience);
        if (modelId) {
          payload.lalModelId = modelId;
        }
      }
      const currentFormData = exportDynamicValues.find(
        (x) => x.exportType === exportType
      );
      if (currentFormData) {
        payload['partnerID'] =
          typeof currentFormData?.partnerID === 'string'
            ? currentFormData.partnerID.trim()
            : '';
        payload['advertiserID'] = currentFormData?.advertiserID || '';
        payload['tvCpm'] = currentFormData?.tvTargeting
          ? parseFloat(currentFormData?.tvTargeting)
          : '';
        payload['metaAdAccountId'] = currentFormData?.metaAdAccountId || '';
        payload['metaLalRatio'] = currentFormData?.metaLalRatio || '';
      }

      switch (exportType) {
        case AUDIENCE_EXPORT_TYPES.OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT: {
          const otsPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'trade_desk',
          };
          resultPayload.push(otsPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.TTD_OTS_SEGMENT_SEED_AUDIENCE:
        case AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE: {
          const otsPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'trade_desk',
            lalModelPrecision: null,
          };
          resultPayload.push(otsPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT: {
          const customPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'liveramp',
            platformDestination: currentFormData?.platformDestination?.id || '',
            advertiserDestination:
              currentFormData?.advertiserDestination?.id || '',
            advertiserDirectPercentageOfMedia:
              currentFormData?.advertiserDirect !== ''
                ? parseInt(currentFormData?.advertiserDirect)
                : null,
          };
          resultPayload.push(customPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE: {
          const customPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'liveramp',
            platformDestination: '',
            advertiserDestination: '',
            advertiserDirectPercentageOfMedia:
              currentFormData?.advertiserDirect !== ''
                ? parseInt(currentFormData?.advertiserDirect)
                : null,
          };
          resultPayload.push(customPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE: {
          const customPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'liveramp',
            platformDestination: currentFormData?.platformDestination?.id || '',
            advertiserDestination:
              currentFormData?.advertiserDestination?.id || '',
            advertiserDirectPercentageOfMedia:
              currentFormData?.advertiserDirect !== ''
                ? parseInt(currentFormData?.advertiserDirect)
                : null,
          };
          resultPayload.push(customPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.TRANSUNION:
        case AUDIENCE_EXPORT_TYPES.TRANSUNION_TTD:
        case AUDIENCE_EXPORT_TYPES.TRANSUNION_HEMS: {
          const customPayload = {
            ...payload,
            exportType,
            platformIntegrated: AUDIENCE_EXPORT_TYPES.TRANSUNION,
          };
          resultPayload.push(customPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.PUBMATIC_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.PUBMATIC_CUSTOM_SEGMENT: {
          const pbPayload = {
            ...payload,
            exportType,
            percentageOfMedia: '',
            platformIntegrated: 'pubmatic',
          };
          resultPayload.push(pbPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.OPENX_OTS_SEGMENT: {
          const opxOtsPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'openx',
            isOts: true,
            category:
              typeof currentFormData?.category === 'string'
                ? currentFormData.category.trim()
                : '',
            subCategory:
              typeof currentFormData?.subCategory === 'string'
                ? currentFormData.subCategory.trim()
                : '',
            type:
              typeof currentFormData?.type === 'string'
                ? currentFormData.type.trim()
                : '',
          };
          resultPayload.push(opxOtsPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.OPENX_CUSTOM_SEGMENT: {
          const opxCustomPayload = {
            ...payload,
            exportType,
            description: '',
            cpm: '',
            platformIntegrated: 'openx',
            percentageOfMedia: '',
            isOts: false,
          };
          resultPayload.push(opxCustomPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.WPP_OTS_SEGMENT: {
          const wppOtsPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'wpp',
            isOts: true,
            // WPP does not use CPM / % of media (CreateWppSegmentRequest)
            cpm: '',
            percentageOfMedia: '',
          };
          resultPayload.push(wppOtsPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.DENTSU_OTS_SEGMENT: {
          const dentsuOtsPayload = {
            ...payload,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.DENTSU_KEY,
            isOts: true,
            // Dentsu OTS requires only taxonomy metadata; modeled values come from LAL when present.
            cpm: '',
            percentageOfMedia: '',
          };
          resultPayload.push(dentsuOtsPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.DENTSU_OTS_SEGMENT_SEED_AUDIENCE: {
          const {
            lalModelPrecision: _lalModelPrecision,
            lalModelType: _lalModelType,
            lalModelId: _lalModelId,
            lalModelPrecisionUsersCount: _lalModelPrecisionUsersCount,
            ...seedBasePayload
          } = payload;
          const dentsuSeedOtsPayload = {
            ...seedBasePayload,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.DENTSU_KEY,
            isOts: true,
            cpm: '',
            percentageOfMedia: '',
          };
          resultPayload.push(dentsuSeedOtsPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT: {
          const opxPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'experian',
          };
          resultPayload.push(opxPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.X_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.X_CUSTOM_SEGMENT: {
          const opxPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'x',
          };
          resultPayload.push(opxPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.META_OTS_SEGMENT: {
          const opxPayload = {
            ...payload,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.META_KEY,
          };
          resultPayload.push(opxPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT: {
          const metaModeledPayload = {
            ...payload,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.META_KEY,
            metaAdAccountId: currentFormData?.metaAdAccountId || '',
            audienceType: audienceType,
          };
          resultPayload.push(metaModeledPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT_SEED_AUDIENCE: {
          const metaPayload = {
            id: audience.id,
            emails: currentUser.email,
            segmentName: segmentName?.trim(),
            description,
            lalModelType: AUDIENCE_EXPORT_LAL_MODAL_TYPE.META,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.META_KEY,
            metaAdAccountId: currentFormData?.metaAdAccountId || '',
            metaLalRatio: currentFormData?.metaLalRatio || '',
            audienceSize: audience?.totalSize || 0,
            seedAudienceCount,
          };
          resultPayload.push(metaPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT_SEED_AUDIENCE: {
          const tiktokPayload = {
            id: audience.id,
            emails: currentUser.email,
            segmentName: segmentName?.trim(),
            description: description?.trim(),
            lalModelType: AUDIENCE_EXPORT_LAL_MODAL_TYPE.TIKTOK,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.TIKTOK_KEY,
            advertiserID: currentFormData?.advertiserID?.trim() || '',
            mobileOs:
              currentFormData?.mobileOs !== undefined
                ? [Number(currentFormData.mobileOs)]
                : [],
            audienceModelingBreadth:
              currentFormData?.audienceModelingBreadth?.trim() || '',
            placements: Array.isArray(currentFormData?.placements)
              ? currentFormData.placements
              : [],
            audienceType: audienceType,
            seedAudienceCount,
          };
          resultPayload.push(tiktokPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.MAGNITE_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.MAGNITE_CUSTOM_SEGMENT: {
          const opxPayload = {
            ...payload,
            exportType,
            platformIntegrated: 'magnite',
          };
          resultPayload.push(opxPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT: {
          const {
            partnerID: _partnerID,
            advertiserID: _advertiserID,
            ...otsPayloadBase
          } = payload;
          const yahooDspPayload = {
            ...otsPayloadBase,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.YAHOO_DSP_KEY,
            tvCpm: parseFloat(currentFormData?.tvCpm),
          };
          resultPayload.push(yahooDspPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT: {
          const yahooDspPayload = {
            ...payload,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.YAHOO_DSP_KEY,
            tvCpm: parseFloat(currentFormData?.tvCpm),
          };
          resultPayload.push(yahooDspPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT: {
          const tiktokPayload = {
            ...payload,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.TIKTOK_KEY,
            advertiserID: currentFormData?.advertiserID?.trim() || '',
            audienceType: audienceType,
          };
          resultPayload.push(tiktokPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.REDDIT_CUSTOM_SEGMENT: {
          const redditPayload = {
            ...payload,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.REDDIT_KEY,
            platformDestination:
              currentFormData?.platformDestination?.trim() || '',
            audienceType: audienceType,
          };
          resultPayload.push(redditPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.AMAZON_CUSTOM_SEGMENT: {
          const amazonPayload = {
            ...payload,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.AMAZON_KEY,
            advertiserID: currentFormData?.advertiserID?.trim() || '',
            audienceType: audienceType,
          };
          resultPayload.push(amazonPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.AMAZON_OTS_SEGMENT: {
          const amazonOtsPayload = {
            ...payload,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.AMAZON_KEY,
            advertiserID: null,
            audienceType: audienceType,
          };
          resultPayload.push(amazonOtsPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.APPSFLYER_CUSTOM_SEGMENT: {
          const appsflyerPayload = {
            ...payload,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.APPSFLYER_KEY,
            advertiserID: currentFormData?.advertiserID?.trim() || '',
            audienceType: audienceType,
          };
          resultPayload.push(appsflyerPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.VIANT_CUSTOM_SEGMENT: {
          const viantPayload = {
            ...payload,
            exportType,
            platformIntegrated: PLATFORMS_KEYS.VIANT_KEY,
            accountID: currentFormData?.accountID?.trim() || '',
            advertiserID: currentFormData?.advertiserID?.trim() || '',
            audienceType: audienceType,
            isOts: false,
          };
          resultPayload.push(viantPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.NEXXEN_OTS_SEGMENT: {
          const nexxenOtsPayload = prepareNexxenPayload({
            audience,
            currentUser,
            segmentName: segmentName || '',
            lalModelType,
            exportType,
            lalModelPrecisionUsersCount,
            lalModelPrecision: payload.lalModelPrecision,
            audienceType,
            currentFormData,
            seedAudienceCount,
            modelScoringType,
            lalModelId: payload.lalModelId as string | undefined,
          });

          resultPayload.push(nexxenOtsPayload);
          break;
        }
        case AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT: {
          const nexxenCustomPayload = prepareNexxenPayload({
            audience,
            currentUser,
            segmentName: segmentName || '',
            lalModelType,
            exportType,
            lalModelPrecisionUsersCount,
            lalModelPrecision: payload.lalModelPrecision,
            audienceType,
            currentFormData,
            seedAudienceCount,
            modelScoringType,
            lalModelId: payload.lalModelId as string | undefined,
          });

          resultPayload.push(nexxenCustomPayload);
          break;
        }
        default:
          break;
      }
    }

    return resultPayload;
  };

  const handleExportToTransunion = (isAudienceComplianceSkipped: boolean) => {
    setIsExporting(true);
    const exportsList = prepareExportPayload();
    const requestBody = {
      isAudienceComplianceSkipped,
      exports: exportsList,
    };
    axios
      .post(`${AUDIENCE_STUDIO_API_ENDPOINT}/export/platform`, requestBody)
      .then(() => {
        clearData(true);
        dispatch(
          addNotification({
            state: 'done',
            message: AUDIENCE_EXPORT_SUCCESS_MESSAGE,
          })
        );

        setTimeout(() => {
          if (audienceType === ENUM_SYSTEM_USED.STANDARD_AUDIENCE) {
            dispatch(fetchAudience({ id: audience.id }));
          } else {
            dispatch(fetchCustomAudience({ id: audience.id }));
          }
        }, 3000);
      })
      .catch((error) => {
        const resData = error.response?.data;
        if (
          error.response?.status === 400 &&
          resData?.complianceErrors != null
        ) {
          setIsExporting(false);
          const violations = resData.complianceErrors?.global?.violations ?? [];
          const bannedOnly = violations.filter(
            (v: { policy?: string }) =>
              v.policy === AUDIENCE_COMPLIANCE_STATUS.FULL_BAN ||
              v.policy === AUDIENCE_COMPLIANCE_STATUS.PARTIAL_BAN
          );
          if (bannedOnly.length === 0) {
            setIsExporting(false);
            dispatch(
              addNotification({
                state: LoadingStates.ERROR,
                message: COMPLIANCE_WARNING_BLOCKED_NO_BAN_ROWS,
              })
            );
            return;
          }
          setComplianceWarningTableData(
            bannedOnly.map(
              (v: {
                category: string;
                policy?: string;
                matchedKeywords?: string[];
                matchedBrands?: string[];
              }) => ({
                category: v.category,
                policy_label:
                  v.policy === AUDIENCE_COMPLIANCE_STATUS.FULL_BAN
                    ? COMPLIANCE_WARNING_POLICY_FULL_BAN
                    : COMPLIANCE_WARNING_POLICY_PARTIAL_BAN,
                matched_keywords: [...(v.matchedKeywords ?? [])],
                matched_brands: [...(v.matchedBrands ?? [])],
              })
            )
          );
          setComplianceWarningOpen(true);
          return;
        }

        clearData(false);

        // Check if the error response contains specific validation errors
        if (resData?.errors) {
          const errors = resData.errors;
          const errorMessages = Object.values(errors).flat();

          // Display all validation error messages merged together
          if (errorMessages.length > 0) {
            const mergedMessages = errorMessages.join(' ');
            dispatch(
              addNotification({
                state: 'error',
                message: mergedMessages,
              })
            );
          } else {
            dispatch(
              addNotification({
                state: 'error',
                message: AUDIENCE_EXPORT_FAILED_MESSAGE,
              })
            );
          }
        } else {
          // Fallback to generic error message
          dispatch(
            addNotification({
              state: 'error',
              message: AUDIENCE_EXPORT_FAILED_MESSAGE,
            })
          );
        }
      });
  };

  /* Util functions | End */

  const {
    isValid: isValidSegmentName,
    validationMessage: inValidSegmentNameErrorMessage,
  } = isSegmentNameValid(
    [...selectedExportTypes, ...selectedSeedAudienceExportTypes],
    segmentName
  );

  const { isValid: isDescriptionValidResult } = isDescriptionValid(
    [...selectedExportTypes, ...selectedSeedAudienceExportTypes],
    description
  );

  const { isValid: isCPMValidResult, cpmErrorMessage } = isCPMValid(
    [...selectedExportTypes, ...selectedSeedAudienceExportTypes],
    cpm
  );

  const {
    isValid: isPercentageOfMediaValidResult,
    percentageOfMediaErrorMessage,
  } = isPercentageOfMediaValid(
    [...selectedExportTypes, ...selectedSeedAudienceExportTypes],
    percentageOfMedia
  );

  // Get advertiserDirect from exportDynamicValues for LiveRamp types
  const advertiserDirectValue = useMemo(() => {
    const allExports = [
      ...selectedExportTypes,
      ...selectedSeedAudienceExportTypes,
    ];
    const isLiveRampType = allExports.some(
      (exportType) =>
        exportType === AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT ||
        exportType === AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT ||
        exportType ===
          AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE ||
        exportType ===
          AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE
    );
    if (isLiveRampType) {
      const currentFormData = exportDynamicValues.find(
        (x) =>
          x.exportType === AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT ||
          x.exportType === AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT ||
          x.exportType ===
            AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE ||
          x.exportType ===
            AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE
      );
      return currentFormData?.advertiserDirect || '';
    }
    return '';
  }, [
    exportDynamicValues,
    selectedExportTypes,
    selectedSeedAudienceExportTypes,
  ]);

  const {
    isValid: isAdvertiserDirectValidResult,
    advertiserDirectErrorMessage,
  } = isAdvertiserDirectValid(
    [...selectedExportTypes, ...selectedSeedAudienceExportTypes],
    advertiserDirectValue
  );

  // Validate TTD Partner ID and Advertiser ID for custom segments
  const isTTDIdValidResult = useMemo(() => {
    const allExports = [
      ...selectedExportTypes,
      ...selectedSeedAudienceExportTypes,
    ];
    const isTTDCustomSegment = allExports.some(
      (exportType) =>
        exportType === AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT ||
        exportType === AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE
    );

    if (!isTTDCustomSegment) {
      return { isValid: true, ttdIdErrorMessage: '' };
    }

    // Find the TTD custom segment form data
    const ttdFormData = exportDynamicValues.find(
      (x) =>
        x.exportType === AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT ||
        x.exportType === AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE
    );

    if (!ttdFormData) {
      return { isValid: true, ttdIdErrorMessage: '' };
    }

    const { partnerID, advertiserID } = ttdFormData;

    // Validate partnerID if provided
    if (partnerID && partnerID.trim() !== '') {
      const partnerIdValidation = isTTDIdValid(partnerID);
      if (!partnerIdValidation.isValid) {
        return partnerIdValidation;
      }
    }

    // Validate advertiserID if provided
    if (advertiserID && advertiserID.trim() !== '') {
      const advertiserIdValidation = isTTDIdValid(advertiserID);
      if (!advertiserIdValidation.isValid) {
        return advertiserIdValidation;
      }
    }

    return { isValid: true, ttdIdErrorMessage: '' };
  }, [
    exportDynamicValues,
    selectedExportTypes,
    selectedSeedAudienceExportTypes,
  ]);

  // Validate Viant Account ID and Advertiser ID (both must be positive integers)
  const isViantIdsValidResult = useMemo(() => {
    const allExports = [
      ...selectedExportTypes,
      ...selectedSeedAudienceExportTypes,
    ];
    const isViantCustomSegment = allExports.some(
      (exportType) => exportType === AUDIENCE_EXPORT_TYPES.VIANT_CUSTOM_SEGMENT
    );

    if (!isViantCustomSegment) {
      return { isValid: true };
    }

    const viantFormData = exportDynamicValues.find(
      (x) => x.exportType === AUDIENCE_EXPORT_TYPES.VIANT_CUSTOM_SEGMENT
    );

    if (!viantFormData) {
      return { isValid: true };
    }

    const { accountID, advertiserID } = viantFormData;

    const accountIdValidation = isViantIdValid(
      accountID || '',
      ValidationErrorMessages.VIANT_ACCOUNT_ID_POSITIVE_ERROR
    );
    if (!accountIdValidation.isValid) {
      return { isValid: false };
    }

    const advertiserIdValidation = isViantIdValid(
      advertiserID || '',
      ValidationErrorMessages.VIANT_ADVERTISER_ID_POSITIVE_ERROR
    );
    if (!advertiserIdValidation.isValid) {
      return { isValid: false };
    }

    return { isValid: true };
  }, [
    exportDynamicValues,
    selectedExportTypes,
    selectedSeedAudienceExportTypes,
  ]);

  // Validate Nexxen dates
  const isNexxenDatesValidResult = useMemo(() => {
    const allExports = [
      ...selectedExportTypes,
      ...selectedSeedAudienceExportTypes,
    ];
    const isNexxenExport = allExports.some(
      (exportType) =>
        exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_OTS_SEGMENT ||
        exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT
    );

    if (!isNexxenExport) {
      return { isValid: true, validationMessage: '' };
    }

    // Find the Nexxen form data
    const nexxenFormData = exportDynamicValues.find(
      (x) =>
        x.exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_OTS_SEGMENT ||
        x.exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT
    );

    if (!nexxenFormData) {
      return { isValid: true, validationMessage: '' };
    }

    const { startDate, endDate } = nexxenFormData;

    return isNexxenDatesValid(startDate || '', endDate);
  }, [
    exportDynamicValues,
    selectedExportTypes,
    selectedSeedAudienceExportTypes,
  ]);

  // Validate Nexxen currency rate (> 0 and <= 30)
  const isNexxenCurrencyRateValidResult = useMemo(() => {
    const allExports = [
      ...selectedExportTypes,
      ...selectedSeedAudienceExportTypes,
    ];
    const isNexxenExport = allExports.some(
      (exportType) =>
        exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_OTS_SEGMENT ||
        exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT
    );

    if (!isNexxenExport) {
      return { isValid: true, validationMessage: '' };
    }

    const nexxenFormData = exportDynamicValues.find(
      (x) =>
        x.exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_OTS_SEGMENT ||
        x.exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT
    );

    if (!nexxenFormData) {
      return { isValid: true, validationMessage: '' };
    }

    const { currencyRate, rateType } = nexxenFormData;

    // FF rate type has no currency rate field
    if (rateType === NEXXEN_RATE_TYPE_FF) {
      return { isValid: true, validationMessage: '' };
    }

    return isCurrencyRateValid(currencyRate);
  }, [
    exportDynamicValues,
    selectedExportTypes,
    selectedSeedAudienceExportTypes,
  ]);

  const isNexxenDestinationIdsValidResult = useMemo(() => {
    const allExports = [
      ...selectedExportTypes,
      ...selectedSeedAudienceExportTypes,
    ];
    const hasNexxenCustom = allExports.some(
      (exportType) => exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT
    );
    if (!hasNexxenCustom) {
      return { isValid: true };
    }
    const nexxenFormData = exportDynamicValues.find(
      (x) => x.exportType === AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT
    );
    if (!nexxenFormData) {
      return { isValid: true };
    }
    if (nexxenFormData.availability !== NEXXEN_AVAILABILITY_SINGLE) {
      return { isValid: true };
    }
    const advOk = validateNexxenAdvertiserDestinationId(
      nexxenFormData.advertiserDestination ?? ''
    ).isValid;
    const platOk = validateNexxenMarketDestinationId(
      nexxenFormData.platformDestination ?? ''
    ).isValid;
    return { isValid: advOk && platOk };
  }, [
    exportDynamicValues,
    selectedExportTypes,
    selectedSeedAudienceExportTypes,
  ]);

  const isRequiredFieldMissing = useMemo(() => {
    if (!selectedExportTypes.length && !selectedSeedAudienceExportTypes.length)
      return true;
    const allExports = [
      ...selectedExportTypes,
      ...selectedSeedAudienceExportTypes,
    ];
    return allExports.some((exportType) => {
      // WPP OTS validates exportDefaultFields only; do not wait for dynamic form init.
      if (exportType === AUDIENCE_EXPORT_TYPES.WPP_OTS_SEGMENT) {
        return areWppOtsRequiredFieldsMissing(
          exportDefaultFields.segmentName,
          exportDefaultFields.description
        );
      }
      if (
        exportType === AUDIENCE_EXPORT_TYPES.DENTSU_OTS_SEGMENT ||
        exportType === AUDIENCE_EXPORT_TYPES.DENTSU_OTS_SEGMENT_SEED_AUDIENCE
      ) {
        return areDentsuOtsRequiredFieldsMissing(
          exportDefaultFields.segmentName,
          exportDefaultFields.description
        );
      }

      const currentFormData = exportDynamicValues.find(
        (x) => x.exportType === exportType
      );
      if (!currentFormData) return false;
      const {
        partnerID,
        advertiserID,
        accountID,
        tvTargeting,
        platformDestination,
        advertiserDestination,
        metaAdAccountId,
        metaLalRatio,
        tvCpm,
        mobileOs,
        audienceModelingBreadth,
        placements,
        availability,
        currencyRate,
        startDate,
        endDate,
        rateType,
      } = currentFormData;
      switch (exportType) {
        case AUDIENCE_EXPORT_TYPES.OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.TTD_OTS_SEGMENT_SEED_AUDIENCE:
          return (
            !exportDefaultFields.segmentName ||
            !exportDefaultFields.cpm ||
            !exportDefaultFields.percentageOfMedia
          );
        case AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE:
          return (
            !exportDefaultFields.segmentName ||
            !exportDefaultFields.cpm ||
            !exportDefaultFields.percentageOfMedia ||
            (!partnerID && !advertiserID)
          );
        case AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE:
        case AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT:
          return (
            !exportDefaultFields.segmentName ||
            !exportDefaultFields.cpm ||
            !tvTargeting ||
            !isCPMValidResult ||
            !isPercentageOfMediaValidResult ||
            !isAdvertiserDirectValidResult
          );
        case AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE:
        case AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT:
          return (
            !exportDefaultFields.segmentName ||
            !exportDefaultFields.cpm ||
            !tvTargeting ||
            (platformDestination && !advertiserDestination) ||
            (!platformDestination && advertiserDestination) ||
            !isCPMValidResult ||
            !isPercentageOfMediaValidResult ||
            !isAdvertiserDirectValidResult
          );
        case AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT:
          return (
            !exportDefaultFields.segmentName ||
            !exportDefaultFields.cpm ||
            !exportDefaultFields.percentageOfMedia ||
            !exportDefaultFields.description?.trim()
          );
        case AUDIENCE_EXPORT_TYPES.X_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.X_CUSTOM_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.META_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.MAGNITE_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.MAGNITE_CUSTOM_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT: {
          const isTvCpmValidResult = isTvCpmValid(exportType, tvCpm);
          return (
            !exportDefaultFields.segmentName?.trim() ||
            _.isEmpty(exportDefaultFields.cpm) ||
            _.isEmpty(tvCpm) ||
            !partnerID?.trim() ||
            !isTvCpmValidResult.isValid
          );
        }
        case AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT: {
          return (
            !exportDefaultFields.segmentName?.trim() ||
            !exportDefaultFields.description?.trim() ||
            !metaAdAccountId
          );
        }
        case AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT: {
          const isTvCpmValidResult = isTvCpmValid(exportType, tvCpm);
          return (
            !exportDefaultFields.segmentName?.trim() ||
            _.isEmpty(exportDefaultFields.cpm) ||
            _.isEmpty(tvCpm) ||
            !isTvCpmValidResult.isValid
          );
        }
        case AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT:
          return (
            !exportDefaultFields.segmentName?.trim() || !advertiserID?.trim()
          );
        case AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT_SEED_AUDIENCE: {
          const isMobileOsValid =
            mobileOs !== undefined && mobileOs !== null && mobileOs !== '';
          return (
            !exportDefaultFields.segmentName?.trim() ||
            !advertiserID?.trim() ||
            !isMobileOsValid ||
            !audienceModelingBreadth?.trim() ||
            !Array.isArray(placements) ||
            placements.length === 0
          );
        }
        case AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT_SEED_AUDIENCE:
          return (
            !exportDefaultFields.segmentName?.trim() ||
            !exportDefaultFields.description?.trim() ||
            !metaAdAccountId ||
            !metaLalRatio
          );
        case AUDIENCE_EXPORT_TYPES.REDDIT_CUSTOM_SEGMENT: {
          return (
            !exportDefaultFields.segmentName?.trim() ||
            !platformDestination?.trim()
          );
        }
        case AUDIENCE_EXPORT_TYPES.AMAZON_CUSTOM_SEGMENT: {
          return (
            !exportDefaultFields.segmentName?.trim() ||
            !exportDefaultFields.description?.trim() ||
            !exportDefaultFields.cpm ||
            !advertiserID?.trim()
          );
        }
        case AUDIENCE_EXPORT_TYPES.AMAZON_OTS_SEGMENT: {
          return (
            !exportDefaultFields.segmentName?.trim() ||
            !exportDefaultFields.description?.trim() ||
            !exportDefaultFields.cpm
          );
        }
        case AUDIENCE_EXPORT_TYPES.APPSFLYER_CUSTOM_SEGMENT: {
          return (
            !exportDefaultFields.segmentName?.trim() || !advertiserID?.trim()
          );
        }
        case AUDIENCE_EXPORT_TYPES.VIANT_CUSTOM_SEGMENT: {
          return (
            !exportDefaultFields.segmentName?.trim() ||
            !accountID?.trim() ||
            !advertiserID?.trim()
          );
        }
        case AUDIENCE_EXPORT_TYPES.NEXXEN_OTS_SEGMENT: {
          const currencyRateValue = parseFloat(currencyRate || '0');
          // If FF: currencyRate should be empty, else currencyRate should be > 0
          const isCurrencyRateInvalid =
            (rateType === NEXXEN_RATE_TYPE_FF &&
              !_.isEmpty(currencyRate?.trim())) ||
            (rateType !== NEXXEN_RATE_TYPE_FF &&
              (_.isEmpty(currencyRate?.trim()) || currencyRateValue <= 0));

          return (
            !exportDefaultFields.segmentName?.trim() ||
            !rateType ||
            isCurrencyRateInvalid ||
            !startDate
          );
        }
        case AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT: {
          const currencyRateValue = parseFloat(currencyRate || '0');
          const isAdvertiserRequired =
            availability === NEXXEN_AVAILABILITY_SINGLE &&
            !advertiserDestination?.trim();
          const isMarketRequired =
            availability === NEXXEN_AVAILABILITY_SINGLE &&
            !platformDestination?.trim();
          // If FF: currencyRate should be empty, else currencyRate should be > 0
          const isCurrencyRateInvalid =
            (rateType === NEXXEN_RATE_TYPE_FF &&
              !_.isEmpty(currencyRate?.trim())) ||
            (rateType !== NEXXEN_RATE_TYPE_FF &&
              (_.isEmpty(currencyRate?.trim()) || currencyRateValue <= 0));

          return (
            !exportDefaultFields.segmentName?.trim() ||
            isAdvertiserRequired ||
            isMarketRequired ||
            !rateType ||
            isCurrencyRateInvalid ||
            !startDate
          );
        }
        case AUDIENCE_EXPORT_TYPES.PUBMATIC_OTS_SEGMENT:
        case AUDIENCE_EXPORT_TYPES.PUBMATIC_CUSTOM_SEGMENT:
          return (
            !exportDefaultFields.segmentName ||
            !exportDefaultFields.cpm ||
            !exportDefaultFields.description?.trim()
          );
        case AUDIENCE_EXPORT_TYPES.OPENX_OTS_SEGMENT:
          return (
            !exportDefaultFields.segmentName ||
            !exportDefaultFields.cpm ||
            !exportDefaultFields.percentageOfMedia ||
            !exportDefaultFields.description?.trim()
          );
        case AUDIENCE_EXPORT_TYPES.OPENX_CUSTOM_SEGMENT:
          return !exportDefaultFields.segmentName;
        default:
          return false;
      }
    });
  }, [
    exportDynamicValues,
    exportDefaultFields,
    selectedExportTypes,
    selectedSeedAudienceExportTypes,
  ]);

  return (
    <>
      <ComplianceWarningModal
        open={complianceWarningOpen}
        onClose={handleComplianceWarningClose}
        onCancel={handleComplianceWarningCancel}
        onConfirm={handleComplianceWarningConfirm}
        data={complianceWarningTableData}
      />
      <div>
        <Box
          sx={{
            flex: 3,
            border: '1px solid #ccc',
            borderRadius: 2,
            margin: '1rem 1rem 2rem 1rem',
            background: 'white',
          }}
        >
          <Typography variant="h2" className={classes.exportTitle}>
            {ACTIVE_AUDIENCES}
          </Typography>
          <DialogContent style={{ overflow: 'hidden' }}>
            <>
              <AudienceExport
                formData={exportDynamicValues}
                exportDynamicFields={exportDynamicFields}
                selectedExportType={selectedExportTypes}
                handleSelectedExportType={handleSelectedExportType}
                selectedSeedAudienceExportTypes={
                  selectedSeedAudienceExportTypes
                }
                segmentName={segmentName}
                isSegmentNameValid={isValidSegmentName}
                inValidSegmentNameErrorMessage={inValidSegmentNameErrorMessage}
                description={description}
                cpm={cpm}
                percentageOfMedia={percentageOfMedia}
                onChange={handleChangeField}
                isSelectAudienceVisible={isSelectAudienceVisible}
                isCPMValid={isCPMValidResult}
                cpmErrorMessage={cpmErrorMessage}
                isPercentageOfMediaValid={isPercentageOfMediaValidResult}
                percentageOfMediaErrorMessage={percentageOfMediaErrorMessage}
              />
              <Box className={classes.exportSeedAudienceContainer}>
                <SeedAudienceExport
                  selectedSeedAudienceExportTypes={
                    selectedSeedAudienceExportTypes
                  }
                  handleSelectedSeedAudienceExportType={
                    handleSelectedSeedAudienceExportType
                  }
                  lalModelType={lalModelType}
                  audienceType={audienceType}
                />
              </Box>
              <div className={classes.exportExportButton}>
                <Tooltip
                  title={
                    isRequiredFieldMissing ? REQUIRED_FIELDS_ARE_MISSING : ''
                  }
                  placement="top"
                  arrow
                >
                  <span>
                    <Button
                      color="primary"
                      disableElevation
                      onClick={() => startExport()}
                      variant="contained"
                      style={{ marginBottom: 15 }}
                      disabled={
                        !selectedExportTypes ||
                        isExporting ||
                        (selectedExportTypes.includes(
                          AUDIENCE_EXPORT_TYPES.KLOVER_IDS
                        ) &&
                          !emails) ||
                        (selectedExportTypes.includes(
                          AUDIENCE_EXPORT_TYPES.RAMP_ID
                        ) &&
                          !emails) ||
                        isRequiredFieldMissing ||
                        !isValidSegmentName ||
                        !isCPMValidResult ||
                        !isPercentageOfMediaValidResult ||
                        !isAdvertiserDirectValidResult ||
                        !isDescriptionValidResult ||
                        !isTTDIdValidResult.isValid ||
                        !isViantIdsValidResult.isValid ||
                        !isNexxenDatesValidResult.isValid ||
                        !isNexxenCurrencyRateValidResult.isValid ||
                        !isNexxenDestinationIdsValidResult.isValid
                      }
                    >
                      {isExporting ? <CircularProgress size={20} /> : 'Export'}
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </>
          </DialogContent>
        </Box>
        <Box
          sx={{
            flex: 3,
            border: '1px solid #ccc',
            borderRadius: 2,
            margin: '0rem 1rem 6rem 1rem',
            background: 'white',
          }}
        >
          <InlineExportHistory audience={audience} />
        </Box>
      </div>
    </>
  );
};

export default InlineAudienceExport;
